import React, { useState } from 'react';
import { Wind, Activity, ChevronDown, ChevronUp, AlertCircle, TrendingUp, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { useAuth } from '../../lib/hooks/useAuth';
import { 
  useMySpirometryHistory,   // ✅ FIX: Use /me hooks for PATIENT role
  useMySixMWTHistory,        // ✅ FIX: Use /me hooks for PATIENT role
  useHomeTestAdherence,
  useDeleteSpirometry,
  useDeleteSixMWT
} from '../../lib/hooks/useTests';
import { useMyScheduledTests } from '../../lib/hooks/useTestSchedules';
import { LoadingState } from '../common/LoadingState';
import { EmptyState } from '../common/EmptyState';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

interface HomeTestsManagementProps {
  onNavigate?: (view: string) => void;
  onLogout?: () => void;
}

interface SpirometryData {
  id: number;
  test_date: string;
  fev1_percent_predicted: number | null;
  fvc_percent_predicted: number | null;
  pef_liters_per_min: number | null;
}

interface SixMWTData {
  id: number;
  test_date: string;
  distance_meters: number | null;
  pre_spo2: number | null;
  pre_heart_rate: number | null;
  post_spo2: number | null;
  post_heart_rate: number | null;
}

interface TestSchedule {
  id: number;
  test_type: 'Spirometry' | '6-Minute Walk Test';
  frequency: string;
  scheduled_times: string[];
  instructions: string;
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  prescription_date: string;
}

export function HomeTestsManagement({ onNavigate, onLogout }: HomeTestsManagementProps = {}) {
  const { user } = useAuth();
  
  // ✅ FIX: Use /me endpoints for PATIENT role (no patient_id needed)
  const { data: spirometryData, isLoading: loadingSpiro } = useMySpirometryHistory({ days: 30 });
  const { data: sixMWTData, isLoading: loading6MWT } = useMySixMWTHistory({ days: 30 });
  
  // Fetch prescribed test schedules
  const { data: scheduledTestsData, isLoading: loadingSchedules } = useMyScheduledTests({ is_active: true });

  // ✅ NEW: Fetch real adherence data from backend
  const { data: adherenceData, isLoading: loadingAdherence } = useHomeTestAdherence(30);
  
  // Delete mutations
  const deleteSpirometry = useDeleteSpirometry();
  const deleteSixMWT = useDeleteSixMWT();

  const [expandedTest, setExpandedTest] = useState<number | null>(null);
  const [expandedResultTest, setExpandedResultTest] = useState<string | null>(null);

  const isLoading = loadingSpiro || loading6MWT || loadingSchedules || loadingAdherence;

  // Use real adherence data from backend
  const adherence = adherenceData ? {
    adherenceRate: adherenceData.adherence_rate,
    completed: adherenceData.completed,
    expectedTests: adherenceData.scheduled,
    pending: adherenceData.pending,
    missed: adherenceData.missed,
    totalPossible: adherenceData.scheduled
  } : {
    adherenceRate: 0,
    completed: 0,
    expectedTests: 0,
    pending: 0,
    missed: 0,
    totalPossible: 0
  };

  // Get risk level based on adherence
  const getRiskLevel = (rate: number) => {
    if (rate >= 80) return { label: 'Low Risk', color: 'text-[#14CAF4]', bgColor: 'bg-[#14CAF4]/10' };
    if (rate >= 50) return { label: 'Medium Risk', color: 'text-[#F07D13]', bgColor: 'bg-[#F07D13]/10' };
    return { label: 'High Risk', color: 'text-destructive', bgColor: 'bg-destructive/10' };
  };

  const riskLevel = getRiskLevel(adherence.adherenceRate);

  // Get adherence status badge
  const getAdherenceStatus = (rate: number) => {
    if (rate >= 80) return { label: 'High', color: 'bg-[#14CAF4] text-white' };
    if (rate >= 50) return { label: 'Medium', color: 'bg-[#F07D13] text-white' };
    return { label: 'Low', color: 'bg-destructive text-white' };
  };

  const adherenceStatus = getAdherenceStatus(adherence.adherenceRate);

  // Get recent test results (most recent 2)
  const spirometryTests = spirometryData?.results || [];
  const sixMWTTests = sixMWTData?.results || [];
  const allTests = [
    ...spirometryTests.map(t => ({ ...t, type: 'Spirometry' as const })),
    ...sixMWTTests.map(t => ({ ...t, type: '6MWT' as const }))
  ].sort((a, b) => new Date(b.test_date).getTime() - new Date(a.test_date).getTime()).slice(0, 2);

  const testSchedules = scheduledTestsData?.schedules || [];

  const handleStartTest = (testType: 'Spirometry' | '6-Minute Walk Test') => {
    if (testType === 'Spirometry') {
      onNavigate?.('spirometry-test');
    } else {
      onNavigate?.('sixmwt-test');
    }
  };

  const handleDeleteTest = (testType: 'Spirometry' | '6MWT', testId: number) => {
    if (testType === 'Spirometry') {
      deleteSpirometry.mutate(testId);  // ✅ FIX: Use .mutate() method from useMutation hook
    } else {
      deleteSixMWT.mutate(testId);  // ✅ FIX: Use .mutate() method from useMutation hook
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading your home tests..." />;
  }

  return (
    <div className="space-y-6 pb-20">
      {/* 30-Day Adherence Card */}
      <Card className="shadow-lg border-2 border-[#F07D13]/20">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-[#F07D13]" />
            <CardTitle className="text-primary">30-Day Home Test Adherence</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Adherence Rate */}
            <div className="text-center">
              <div className="text-5xl text-[#F07D13] mb-2">{adherence.adherenceRate}%</div>
              <p className="text-muted-foreground mb-2">Adherence Rate</p>
              <Badge className={adherenceStatus.color}>{adherenceStatus.label}</Badge>
            </div>

            {/* Risk Level */}
            <div className="text-center">
              <AlertCircle className={`h-12 w-12 mx-auto mb-2 ${riskLevel.color}`} />
              <p className="text-muted-foreground mb-2">Risk Level</p>
              <div className={`inline-flex items-center px-3 py-1 rounded-full ${riskLevel.bgColor} ${riskLevel.color}`}>
                {riskLevel.label}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Please maintain test schedule</p>
            </div>

            {/* Progress Bars */}
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Completed</span>
                  <span className="text-[#14CAF4]">{adherence.completed}/{adherence.expectedTests}</span>
                </div>
                <Progress value={(adherence.completed / adherence.expectedTests) * 100} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Pending</span>
                  <span className="text-[#F07D13]">{adherence.pending}</span>
                </div>
                <Progress value={(adherence.pending / adherence.expectedTests) * 100} className="h-2 [&>div]:bg-[#F07D13]" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Missed</span>
                  <span className="text-destructive">{adherence.missed}/{adherence.totalPossible}</span>
                </div>
                <Progress value={(adherence.missed / adherence.totalPossible) * 100} className="h-2 [&>div]:bg-destructive" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Test Results */}
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#14CAF4]" />
            <CardTitle className="text-primary">Recent Test Results</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {allTests.length === 0 ? (
            <EmptyState
              title="No test results yet"
              description="Complete your prescribed tests to see results here"
              icon={Activity}
            />
          ) : (
            allTests.map((test) => (
              <div key={`${test.type}-${test.id}`} className="p-4 border border-border rounded-xl">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {test.type === 'Spirometry' ? (
                      <Wind className="h-5 w-5 text-[#14CAF4]" />
                    ) : (
                      <Activity className="h-5 w-5 text-[#14CAF4]" />
                    )}
                    <div>
                      <p className="text-primary">{test.type === 'Spirometry' ? 'Spirometry Test' : '6-Minute Walk Test'}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(parseISO(test.test_date), 'MMMM do, yyyy \'at\' HH:mm')}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-[#14CAF4] text-white">Completed</Badge>
                </div>

                {test.type === 'Spirometry' ? (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">FEV1</p>
                      <p className="text-primary">{(test as any).fev1_percent_predicted || 0}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">FVC</p>
                      <p className="text-primary">{(test as any).fvc_percent_predicted || 0}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">PEF</p>
                      <p className="text-primary">{(test as any).pef_liters_per_min || 0}%</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Distance</p>
                      <p className="text-primary">{(test as any).distance_meters || 0}m</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">SpO2</p>
                      <p className="text-primary">{(test as any).pre_spo2 || 0}% → {(test as any).post_spo2 || 0}%</p>
                    </div>
                  </div>
                )}
                <div className="flex justify-end">
                  <Button
                    className="bg-red-500 hover:bg-red-600 text-white"
                    onClick={() => handleDeleteTest(test.type, test.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Prescribed Home Tests */}
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-[#2762AC]" />
            <CardTitle className="text-primary">Prescribed Home Tests</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {testSchedules.length === 0 ? (
            <EmptyState
              title="No tests prescribed yet"
              description="Your therapist will prescribe home tests for you"
              icon={Activity}
            />
          ) : (
            testSchedules.map((schedule) => (
              <Collapsible
                key={schedule.id}
                open={expandedTest === schedule.id}
                onOpenChange={(open) => setExpandedTest(open ? schedule.id : null)}
              >
                <div className="border border-border rounded-xl overflow-hidden">
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer">
                      <div className="flex items-center gap-3">
                        {schedule.test_type === 'Spirometry' ? (
                          <Wind className="h-5 w-5 text-[#14CAF4]" />
                        ) : (
                          <Activity className="h-5 w-5 text-[#14CAF4]" />
                        )}
                        <div>
                          <p className="text-primary">{schedule.test_type}</p>
                          <p className="text-sm text-muted-foreground">{schedule.frequency}</p>
                        </div>
                      </div>
                      {expandedTest === schedule.id ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="px-4 pb-4 space-y-4 border-t border-border pt-4">
                      {/* Scheduled Times */}
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Scheduled Times:</p>
                        <div className="flex flex-wrap gap-2">
                          {schedule.scheduled_times && schedule.scheduled_times.length > 0 ? (
                            schedule.scheduled_times.map((time, idx) => (
                              <Badge key={idx} variant="outline" className="border-[#14CAF4] text-[#14CAF4]">
                                {time}
                              </Badge>
                            ))
                          ) : (
                            <Badge variant="outline">As needed</Badge>
                          )}
                        </div>
                      </div>

                      {/* Instructions */}
                      {schedule.test_type === 'Spirometry' && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-2">Instructions:</p>
                          <div className="flex flex-wrap gap-2 mb-2">
                            <Badge variant="outline">FEV1</Badge>
                            <Badge variant="outline">FVC</Badge>
                            <Badge variant="outline">PEF</Badge>
                          </div>
                        </div>
                      )}

                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Instructions:</p>
                        <p className="text-sm text-foreground">
                          {schedule.instructions || 
                            (schedule.test_type === 'Spirometry' 
                              ? 'Perform spirometry test in the morning before medication. Take 3 readings and record the best result.'
                              : 'Walk for 6 minutes at your comfortable pace. Rest if needed but keep the timer running.'
                            )
                          }
                        </p>
                      </div>

                      {/* Dates */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Prescription Date:</p>
                          <p className="text-foreground">
                            {schedule.prescription_date 
                              ? format(parseISO(schedule.prescription_date), 'MMMM dd, yyyy')
                              : 'N/A'
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Start Date:</p>
                          <p className="text-foreground">
                            {format(parseISO(schedule.start_date), 'MMMM dd, yyyy')}
                          </p>
                        </div>
                      </div>

                      {/* Action Button */}
                      <Button 
                        className="w-full bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-white"
                        onClick={() => handleStartTest(schedule.test_type)}
                      >
                        Start {schedule.test_type}
                      </Button>
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}